import React, { useRef } from 'react';
import { Icon } from '../../Icons';
import { ABOUT_INFO, SKILLS } from '../../../utils/constants';
import styles from '../AppContent.module.css';
import localStyles from './Resume.module.css';

// Google Docs resume link
const RESUME_URL = 'https://docs.google.com/document/d/1J-eiI-Abq5GUYLHp1jS3oZ-be4IBj8_qhlpSZaUuxsM/edit?tab=t.0';

const experience = [
  {
    title: 'Co-Founder & Software Engineer',
    company: 'Wattnest (SkyDeck Pad13 Batch 21)',
    period: 'Apr 2025 - Present',
    description: 'Building plug-and-play smart controllers for industrial equipment retrofits via CTA-2045/Modbus. Real-time energy monitoring, fault detection, and BMS integration. Pilot deployments at UC campus facilities.',
  },
  {
    title: 'Software Engineer (Contract)',
    company: 'Gullie',
    period: 'Nov 2025 - Dec 2025',
    description: 'Built Letta AI agent monitoring infrastructure. Real-time memory visualization dashboard with polling, recursive JSON diff algorithms, and conversation tracking.',
  },
  {
    title: 'Software Engineer Intern',
    company: 'Nova Measuring Instruments',
    period: 'Jul 2025 - Sep 2025',
    description: 'C#, Python, .NET development with MVVM architecture for metrology and semiconductor tooling applications.',
  },
  {
    title: 'Software Engineer Intern',
    company: 'Lawrence Berkeley National Laboratory',
    period: 'May 2023 - Oct 2024',
    description: 'ML development for PG&E/SCE Wattersaver program using DTW, DBA, TSLearn for HPWH signal clustering (95% accuracy). LSTM load forecasting, Spark/Scala ETL pipelines. Co-authored 3 publications (IEEE ICDCM, ACEEE, IEPEC).',
  },
];

const education = [
  {
    degree: 'B.S. Computer Science Engineering',
    school: 'UC Santa Cruz',
    year: 'Sep 2024 - Jun 2026',
    details: 'ML, Deep Learning, Computer Networks, Computer Architecture. Parliamentarian, Student Union Assembly. Senator, Cowell College.',
  },
  {
    degree: 'A.S. Computer & Electrical Engineering',
    school: 'Berkeley City College',
    year: 'Aug 2022 - May 2024',
    details: 'GPA 3.8. President, Electronics & Engineering Club. Chief Justice, Student Government. VP, Berkeley Applied Mathematics Club.',
  },
];

export const Resume: React.FC = () => {
  const resumeRef = useRef<HTMLDivElement>(null);

  const openResumePopup = () => {
    window.open(RESUME_URL, '_blank', 'width=900,height=700,scrollbars=yes,resizable=yes');
  };

  const handleDownload = () => {
    openResumePopup();
  };

  const handlePrint = () => {
    openResumePopup();
  };

  return (
    <div className={styles.appContainer}>
      <div className={styles.toolbar}>
        <button className={styles.toolbarButton} onClick={handleDownload}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M14 11V14H2V11H0V14C0 15.1 0.9 16 2 16H14C15.1 16 16 15.1 16 14V11H14Z"/>
            <path d="M13 7L11.59 5.59L9 8.17V0H7V8.17L4.41 5.59L3 7L8 12L13 7Z"/>
          </svg>
          Download PDF
        </button>
        <button className={styles.toolbarButton} onClick={handlePrint}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M13 4H3C1.34 4 0 5.34 0 7V12H3V16H13V12H16V7C16 5.34 14.66 4 13 4ZM11 14H5V10H11V14ZM13 8C12.45 8 12 7.55 12 7C12 6.45 12.45 6 13 6C13.55 6 14 6.45 14 7C14 7.55 13.55 8 13 8ZM13 2H3V0H13V2Z"/>
          </svg>
          Print
        </button>
        <button className={styles.toolbarButton} onClick={openResumePopup}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M14 16H2C0.9 16 0 15.1 0 14V2C0 0.9 0.9 0 2 0H6V2H2V14H14V10H16V14C16 15.1 15.1 16 14 16Z"/>
            <path d="M8 0V2H12.59L5.29 9.29L6.71 10.71L14 3.41V8H16V0H8Z"/>
          </svg>
          Open in New Tab
        </button>
      </div>

      <div className={styles.contentPanel}>
        <div className={localStyles.resume} ref={resumeRef}>
          {/* Header */}
          <div className={localStyles.header}>
            <h1>{ABOUT_INFO.name}</h1>
            <p className={localStyles.headerTitle}>{ABOUT_INFO.title}</p>
            <div className={localStyles.headerContact}>
              <span>{ABOUT_INFO.email}</span>
              <span>|</span>
              <span>{ABOUT_INFO.location}</span>
            </div>
          </div>

          {/* Summary */}
          <section className={localStyles.section}>
            <h2 className={localStyles.sectionTitle}>
              <Icon name="user" size={20} />
              Professional Summary
            </h2>
            <p className={localStyles.summary}>
              Senior Computer Engineering student at UC Santa Cruz with experience spanning full-stack development,
              energy systems research, and AI/ML applications. Co-founder of Wattnest (SkyDeck-backed) building
              smart controllers for industrial equipment. 1.5 years at Lawrence Berkeley National Laboratory
              developing ML tools for grid flexibility programs. Co-authored 3 peer-reviewed publications on
              energy efficiency and demand response (IEEE ICDCM, ACEEE, IEPEC).
            </p>
          </section>

          {/* Experience */}
          <section className={localStyles.section}>
            <h2 className={localStyles.sectionTitle}>
              <Icon name="folder" size={20} />
              Experience
            </h2>
            {experience.map((job, index) => (
              <div key={index} className={localStyles.item}>
                <div className={localStyles.itemHeader}>
                  <div>
                    <h3 className={localStyles.itemTitle}>{job.title}</h3>
                    <p className={localStyles.itemCompany}>{job.company}</p>
                  </div>
                  <span className={localStyles.itemPeriod}>{job.period}</span>
                </div>
                <p className={localStyles.itemDescription}>{job.description}</p>
              </div>
            ))}
          </section>

          {/* Education */}
          <section className={localStyles.section}>
            <h2 className={localStyles.sectionTitle}>
              <Icon name="document" size={20} />
              Education
            </h2>
            {education.map((edu, index) => (
              <div key={index} className={localStyles.item}>
                <div className={localStyles.itemHeader}>
                  <div>
                    <h3 className={localStyles.itemTitle}>{edu.degree}</h3>
                    <p className={localStyles.itemCompany}>{edu.school}</p>
                  </div>
                  <span className={localStyles.itemPeriod}>{edu.year}</span>
                </div>
                {edu.details && (
                  <p className={localStyles.itemDescription}>{edu.details}</p>
                )}
              </div>
            ))}
          </section>

          {/* Skills */}
          <section className={localStyles.section}>
            <h2 className={localStyles.sectionTitle}>
              <Icon name="settings" size={20} />
              Technical Skills
            </h2>
            <div className={localStyles.skills}>
              {SKILLS.map((skill) => (
                <span key={skill.name} className={localStyles.skillTag}>
                  {skill.name}
                </span>
              ))}
            </div>
          </section>
        </div>
      </div>

      <div className={styles.statusBar}>
        <span>Resume</span>
      </div>
    </div>
  );
};

export default Resume;
